import React, { useEffect, useState } from 'react';
import { Shield, ArrowRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const SNOOZE_STORAGE_KEY = 'receevit_two_factor_snooze_until';
const SNOOZE_DURATION_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

const TwoFactorReminderBanner = ({ user }) => {
  const [state, setState] = useState({ loading: true, show: false, data: null });
  const navigate = useNavigate();

  useEffect(() => {
    const loadTwoFactorStatus = async () => {
      if (!user || user.role === 'additional_user') {
        setState({ loading: false, show: false, data: null });
        return;
      }

      const snoozeUntil = localStorage.getItem(SNOOZE_STORAGE_KEY);
      if (snoozeUntil && new Date(snoozeUntil) > new Date()) {
        setState({ loading: false, show: false, data: null });
        return;
      }

      try {
        const response = await api.get('/users/security');
        if (response.data.success) {
          const twoFactor = response.data.data?.twoFactor;
          if (twoFactor && !twoFactor.enabled) {
            setState({ loading: false, show: true, data: twoFactor });
          } else {
            setState({ loading: false, show: false, data: twoFactor });
          }
        } else {
          setState({ loading: false, show: false, data: null });
        }
      } catch (error) {
        console.error('TwoFactorReminderBanner: failed to load security status', error);
        setState({ loading: false, show: false, data: null });
      }
    };

    loadTwoFactorStatus();
    const handleTwoFactorUpdated = () => {
      loadTwoFactorStatus();
    };
    window.addEventListener('twofactor:updated', handleTwoFactorUpdated);
    return () => {
      window.removeEventListener('twofactor:updated', handleTwoFactorUpdated);
    };
  }, [user]);

  if (!state.show || state.loading) {
    return null;
  }

  const { data } = state;
  const isReconfigure = Boolean(data?.reconfigureRequired);
  const daysRemaining = typeof data?.daysRemaining === 'number' ? data.daysRemaining : null;
  const graceText = isReconfigure
    ? 'Two-factor authentication must be reconfigured now to keep your account secure.'
    : daysRemaining !== null
      ? daysRemaining > 0
        ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining to complete setup.`
        : 'Grace period has ended. Setup is required now.'
      : 'Complete setup as soon as possible.';

  const handleNavigateToSettings = () => {
    localStorage.setItem('settings_active_tab', 'security');
    localStorage.removeItem(SNOOZE_STORAGE_KEY);
    navigate('/portal/settings');
  };

  const handleSnooze = () => {
    const snoozeUntil = new Date(Date.now() + SNOOZE_DURATION_MS).toISOString();
    localStorage.setItem(SNOOZE_STORAGE_KEY, snoozeUntil);
    setState(prev => ({ ...prev, show: false }));
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 md:p-5 shadow-sm mb-6">
      <div className="flex items-start md:items-center gap-3 md:gap-4">
        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
            <Shield className="w-5 h-5" />
        </div>
        <div className="flex-1">
            <h3 className="text-sm font-semibold text-indigo-900 mb-1">
              {isReconfigure
                ? 'Reconfigure two-factor authentication to keep access'
                : 'Protect your account with two-factor authentication'}
            </h3>
            <p className="text-sm text-indigo-800 mb-2">
              {isReconfigure
                ? 'You previously completed two-factor authentication. For security, you must reconfigure it now to continue using Receevit®.'
                : 'Enable two-factor authentication to secure your Receevit® account. You will need an authenticator app to generate one-time codes when signing in.'}
            </p>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center text-xs text-indigo-700 font-medium gap-1">
              <Clock className="w-4 h-4" />
              {graceText}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleNavigateToSettings}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg shadow hover:bg-indigo-700 transition"
              >
                Go to Security
                <ArrowRight className="w-4 h-4" />
              </button>
              {!isReconfigure && (
                <button
                  onClick={handleSnooze}
                  className="text-xs font-medium text-indigo-700 hover:text-indigo-900"
                >
                  Remind me later
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorReminderBanner;

